import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { startOnlinePayment } from "@/lib/payments";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export function PayOnlineDialog({ invoice, onClose, onPaid }) {
  const [providers, setProviders] = useState(null);
  const [provider, setProvider] = useState("");
  const [amount, setAmount] = useState(invoice?.balance || 0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/providers").then((r) => { setProviders(r.data); setProvider(r.data[0]?.id || ""); }).catch(() => setProviders([]));
  }, []);
  useEffect(() => { setAmount(invoice?.balance || 0); }, [invoice]);

  const amt = Number(amount);
  const valid = amt >= 1 && amt <= (invoice?.balance || 0) && provider;

  async function pay() {
    setBusy(true);
    try {
      const res = await startOnlinePayment({ invoice, amount: amt, provider });
      if (res.status === "paid") { toast.success(`Payment of ₹${amt.toFixed(0)} received. Receipt ${res.receipt_no}`); onPaid?.(); onClose(); }
      else if (res.status === "failed") toast.error("Payment failed. No money was taken.");
      else if (res.status === "cancelled") toast("Payment cancelled");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={!!invoice} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="pay-online-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><CreditCard size={20} /> Pay fees online</DialogTitle></DialogHeader>
        {invoice && (
          <div className="space-y-4 pt-1">
            <div className="rounded-xl bg-violet-50 border border-violet-100 p-4 text-sm">
              <div className="font-semibold text-slate-900">{invoice.period} • {invoice.receipt_no}</div>
              <div className="text-slate-600 mt-1">Balance due <span className="font-bold text-rose-700">₹{invoice.balance.toFixed(0)}</span></div>
            </div>
            <div>
              <Label>Amount to pay (₹)</Label>
              <Input data-testid="pay-online-amount" type="number" min={1} max={invoice.balance} value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 input-focus" />
              {amt > invoice.balance && <p className="text-xs text-rose-600 mt-1">Cannot be more than the balance.</p>}
            </div>
            {providers === null ? <Loader2 className="animate-spin text-violet-500" /> : providers.length === 0 ? (
              <p data-testid="pay-online-unavailable" className="text-sm text-slate-500">Online payment is not available yet. Please pay at the school office.</p>
            ) : (
              <div>
                <Label>Pay with</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {providers.map((p) => (
                    <button key={p.id} type="button" data-testid={`pay-provider-${p.id}`} onClick={() => setProvider(p.id)}
                      className={`rounded-lg border px-3 py-3 text-sm font-semibold transition-colors ${provider === p.id ? "border-violet-600 bg-violet-50 text-violet-800" : "border-slate-200 text-slate-600 hover:border-violet-300"}`}>
                      {p.label}<div className="text-[11px] font-normal text-slate-500">UPI • Cards • Netbanking</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Button data-testid="pay-online-submit" onClick={pay} disabled={!valid || busy} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 size={18} className="animate-spin" /> : `Pay ₹${(amt || 0).toFixed(0)}`}
            </Button>
            <p className="text-[11px] text-slate-400 flex items-center gap-1"><ShieldCheck size={12} /> Secure checkout. Your card or UPI details are never stored by the school.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
